// The `{Slicer.` menu's carrier and list. An antd Dropdown portals the list to the body
// and takes its z-index from the Modal's context, so the list sits above the Add Widget
// modal and flips near the viewport bottom without a hand-rolled portal.

import { useEffect, useRef } from "react";
import { Dropdown } from "antd";
import { optionId, type SlicerMenuHeader, type SlicerMenuItem, type SlicerMenuModel } from "./useSlicerTokenTrigger";

// The list root keeps the field's focus and caret on a click (the EditorToolbar idiom).
const preventBlur = (e: React.MouseEvent) => e.preventDefault();

function Disc({ color }: { color: string }) {
  return <span className="slicer-badge-disc slicer-menu-disc" style={{ background: color }} />;
}

function HeaderRow({ header }: { header: SlicerMenuHeader }) {
  return (
    <div className="slicer-menu-header">
      <Disc color={header.color} />
      <span className="slicer-menu-label">{header.label}</span>
      <span className="slicer-menu-key">{header.key}</span>
      <span className="slicer-menu-kind">{header.kindLabel}</span>
    </div>
  );
}

function RowContent({ item }: { item: SlicerMenuItem }) {
  if (item.kind === "slicer") {
    return (
      <>
        <Disc color={item.color} />
        <span className="slicer-menu-label">{item.label}</span>
        <span className="slicer-menu-key">{item.key}</span>
        <span className="slicer-menu-kind">{item.kindLabel}</span>
      </>
    );
  }
  if (item.kind === "variable") {
    return (
      <>
        <span className="slicer-menu-name">{item.name}</span>
        <span className="slicer-menu-dot">·</span>
        <span className={item.blank ? "slicer-menu-value is-blank" : "slicer-menu-value"}>{item.valueText}</span>
      </>
    );
  }
  return null;
}

interface SlicerTokenListProps {
  model: SlicerMenuModel;
  activeIndex: number;
  listId: string;
  onPick: (index: number) => void;
  onHover: (index: number) => void;
  minWidth?: number;
}

function SlicerTokenList({ model, activeIndex, listId, onPick, onHover, minWidth }: SlicerTokenListProps) {
  const activeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, model]);

  return (
    <div className="slicer-menu" role="listbox" id={listId} style={{ minWidth }} onMouseDown={preventBlur}>
      {model.header && <HeaderRow header={model.header} />}
      {model.items.map((item, index) => {
        if (item.kind === "empty") {
          return (
            <div key={`empty-${index}`} className="slicer-menu-empty" role="option" aria-disabled aria-selected={false}>
              <div>{item.text}</div>
              {item.detail && <div className="slicer-menu-empty-detail">{item.detail}</div>}
            </div>
          );
        }
        const active = index === activeIndex;
        return (
          <div
            key={item.id}
            id={optionId(listId, index)}
            ref={active ? activeRef : undefined}
            role="option"
            aria-selected={active}
            className={active ? "slicer-menu-row is-active" : "slicer-menu-row"}
            onMouseMove={() => onHover(index)}
            onClick={() => onPick(index)}
          >
            <RowContent item={item} />
          </div>
        );
      })}
      {model.footer && (
        <div className="slicer-menu-footer">
          {model.footer.text}
          <code>{model.footer.example}</code>
          {model.footer.tail}
        </div>
      )}
    </div>
  );
}

export interface SlicerTokenMenuProps {
  open: boolean;
  model: SlicerMenuModel | null;
  activeIndex: number;
  listId: string;
  onPick: (index: number) => void;
  onHover: (index: number) => void;
  /** The list is at least this wide; the title field passes its own width. */
  minWidth?: number;
  /** The anchor the list opens under: the title field's wrapper, or the editor's anchor span. */
  children: React.ReactElement;
}

export function SlicerTokenMenu({ open, model, activeIndex, listId, onPick, onHover, minWidth, children }: SlicerTokenMenuProps) {
  return (
    <Dropdown
      open={open && model != null}
      trigger={[]}
      placement="bottomLeft"
      destroyOnHidden
      popupRender={() =>
        model ? (
          <SlicerTokenList
            model={model}
            activeIndex={activeIndex}
            listId={listId}
            onPick={onPick}
            onHover={onHover}
            minWidth={minWidth}
          />
        ) : (
          <div />
        )
      }
    >
      {children}
    </Dropdown>
  );
}
